import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import type { UUID } from "@/app/api/types";
import {
  EmployeeCreate,
  EmployeeUpdate,
  EmployeeResponse,
  EmployeeLinkRequest,
} from "@/app/types";

// Query keys
export const employeeKeys = {
  all: ["employees"] as const,
  lists: () => [...employeeKeys.all, "list"] as const,
  list: () => [...employeeKeys.lists()] as const,
  // Employees scoped to a group (agency workspace). Under `all` so the shared
  // member-graph invalidation refreshes it too.
  forGroup: (groupId: string) =>
    [...employeeKeys.all, "group", groupId] as const,
  details: () => [...employeeKeys.all, "detail"] as const,
  detail: (id: UUID) => [...employeeKeys.details(), id] as const,
} as const;

/**
 * Fetch all employees for the current company
 */
export function useEmployees(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: employeeKeys.list(),
    queryFn: async () => {
      const employees = await api.employees.get();

      // Cache each employee individually for lookup
      employees.forEach((employee) => {
        queryClient.setQueryData(employeeKeys.detail(employee.id), employee);
      });

      return employees;
    },
    enabled: options?.enabled ?? true,
    staleTime: 30 * 60 * 1000, // 30 minutes — employees rarely change
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Employees scoped to a single group (the agency workspace). Hits the
 * server-side scoped endpoint (unlinked + linked-to-member) so a group admin
 * never receives out-of-scope records over the wire — this replaces a
 * client-side filter that was not a real access boundary.
 */
export function useGroupEmployees(
  groupId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: employeeKeys.forGroup(groupId ?? "none"),
    queryFn: () => api.employees.getForGroup(groupId as UUID),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/**
 * Fetch a single employee by ID
 */
export function useEmployee(id: UUID | null, options?: { staleTime?: number }) {
  return useQuery({
    queryKey: employeeKeys.detail(id!),
    queryFn: () => api.employees.getEmployeeId(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create a new employee
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmployeeCreate) => api.employees.post(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}

/**
 * Update an employee
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: EmployeeUpdate }) =>
      api.employees.putEmployeeId(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
}

/**
 * Delete an employee
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.employees.deleteEmployeeId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}

/**
 * Link an employee to a user account
 */
export function useLinkEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: EmployeeLinkRequest }) =>
      api.employees.postEmployeeIdLink(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
}

/**
 * Unlink an employee from a user account
 */
export function useUnlinkEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.employees.postEmployeeIdUnlink(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
}

/**
 * Update the current user's linked employee
 */
export function useUpdateMyEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EmployeeUpdate) => api.employees.putMe(data),
    onSuccess: (updatedEmployee) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      queryClient.invalidateQueries({
        queryKey: employeeKeys.detail(updatedEmployee.id),
      });
    },
  });
}
